import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

/**
 * 轻量下拉弹层 —— 主图「指标 ▾」「副图 ▾」与 MA 周期设置共用。
 *
 * 为什么不用原生 `<select>`：这三处都是**多选**（MA/BOLL/BBI/估值共存、
 * 副图 3 槽位、MA 周期任意组合），原生 select 无法表达。而新建一套
 * 自绘下拉若每个组件各写一份，会出现三套点击外部/Escape/焦点处理。
 * 故抽成此原语，全项目仅此一处实现该行为。
 *
 * 定位：面板绝对定位在触发器下方。ChartZone 的工具栏（第 177 行附近）
 * **没有** overflow-hidden，而 overflow-hidden 在其下方的图表容器上 ——
 * 面板不会被裁剪，只需 z-index 高于图表 canvas。
 *
 * 可达性：触发器为真实 `<button>`（aria-expanded/aria-haspopup），
 * 面板内用原生 checkbox / button，Tab 可达；Escape 关闭并把焦点还给触发器。
 */
export function Popover({
  label,
  /** 触发器右侧的摘要，如「MA·BOLL」；不传则只显示 label */
  summary,
  /** 摘要为空时的占位符，用于「当前未启用任何指标」这类状态 */
  placeholder,
  badge,
  width = 240,
  title,
  variant = 'chip',
  children,
}: {
  label: string;
  summary?: string;
  placeholder?: string;
  /** 触发器上的计数角标，如「3」 */
  badge?: number;
  width?: number;
  title?: string;
  /**
   * 触发器外观：
   *  · chip —— 工具栏那种带边框的小按钮（「指标 ▾」「副图 ▾」），默认
   *  · bare —— 无边框的标题形态，给图表页头的指数名用（15px 半粗、悬停仅加底色）
   */
  variant?: 'chip' | 'bare';
  children: ReactNode | ((close: () => void) => ReactNode);
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  /** 面板左移量：触发器贴近右边缘时不左移就会溢出视口 */
  const [shift, setShift] = useState(0);

  // 面板是相对触发器 left:0 定位的，触发器靠右时必然溢出。
  // 按实际面板宽度把它左移回视口内（窄屏下由 max-width 夹住宽度）。
  useEffect(() => {
    if (!open) {
      setShift(0);
      return;
    }
    const clamp = () => {
      const t = btnRef.current?.getBoundingClientRect();
      const p = panelRef.current;
      if (!t || !p) return;
      const margin = 12;
      const overflow = t.left + p.offsetWidth - (window.innerWidth - margin);
      setShift(overflow > 0 ? overflow : 0);
    };
    clamp();
    window.addEventListener('resize', clamp);
    return () => window.removeEventListener('resize', clamp);
  }, [open, width]);

  useEffect(() => {
    if (!open) return;
    /** 点击外部：pointerdown 而非 click，避免先触发内部控件再关闭 */
    const onDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      // 焦点还给触发器，否则关闭后焦点落到 body，键盘用户会"丢失位置"
      btnRef.current?.focus();
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const bare = variant === 'bare';

  return (
    <div ref={wrapRef} className="relative inline-flex">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? panelId : undefined}
        title={title ?? label}
        className={bare
          ? 'flex items-center gap-1 rounded px-1 py-0.5 text-[15px] font-semibold transition-colors hover:bg-[var(--bg-hover)]'
          : 'flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] transition-colors hover:border-[var(--accent)]'}
        style={bare ? { color: 'var(--text-1)' } : { borderColor: 'var(--border)', color: 'var(--text-2)' }}
      >
        <span>{label}</span>
        {badge !== undefined && badge > 0 && (
          <span
            className="num rounded px-1 text-[10px]"
            style={{ background: 'var(--bg-elevated)', color: 'var(--text-1)' }}
          >
            {badge}
          </span>
        )}
        {summary !== undefined && (
          <span className="num max-w-[96px] truncate" style={{ color: 'var(--text-3)' }}>
            {summary || placeholder}
          </span>
        )}
        <span className={bare ? 'text-[10px]' : 'text-[9px]'} style={{ color: 'var(--text-3)' }} aria-hidden>▾</span>
      </button>

      {open && (
        <div
          id={panelId}
          ref={panelRef}
          role="dialog"
          aria-label={label}
          className="absolute top-[calc(100%+4px)] z-50 rounded-[6px] border p-2 shadow-lg"
          style={{
            left: shift > 0 ? -shift : 0,
            width,
            // 宽度是固定 px，窄屏会溢出视口；夹一下让内容内部换行
            maxWidth: 'calc(100vw - 24px)',
            background: 'var(--bg-surface)',
            borderColor: 'var(--border-strong)',
            boxShadow: '0 8px 24px rgb(0 0 0 / 0.35)',
          }}
        >
          {typeof children === 'function' ? children(() => setOpen(false)) : children}
        </div>
      )}
    </div>
  );
}

/** 弹层内的一行勾选项 —— 样式与语义统一，避免各处复写 checkbox 样式 */
export function PopoverCheck({
  checked,
  onChange,
  disabled,
  label,
  title,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: ReactNode;
  title?: string;
}) {
  return (
    <label
      className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-[12px] transition-colors hover:bg-[var(--bg-hover)]"
      style={{ opacity: disabled ? 0.45 : 1, cursor: disabled ? 'not-allowed' : undefined }}
      title={title}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 shrink-0 accent-[var(--accent)]"
      />
      <span style={{ color: checked ? 'var(--text-1)' : 'var(--text-3)' }}>{label}</span>
    </label>
  );
}

/** 弹层内的分组小标题 */
export function PopoverGroup({ children }: { children: ReactNode }) {
  return (
    <div className="mt-1.5 mb-0.5 text-[10px] first:mt-0" style={{ color: 'var(--text-3)' }}>
      {children}
    </div>
  );
}